import React, { useState, useEffect, useCallback } from 'react';
import Overlay from './Overlay';
import styles from './Lightbox.module.css';

/**
 * Fullscreen photo viewer, shared by the admin Media library and the parent
 * gallery. Photos only — videos keep their inline native controls (incl. native
 * fullscreen), so the lightbox never fights a video scrubber.
 *
 * Each open is a fresh mount (callers render `{state && <Lightbox .../>}`), so
 * internal `current` initialises from `index` and then owns navigation. Pass a
 * STABLE `photos` array (capture it in state at click time, don't rebuild it
 * each render) so re-renders while open don't disturb the current photo.
 *
 *   const [lightbox, setLightbox] = useState(null); // { photos, index, caption } | null
 *   <img onClick={() => setLightbox({ photos, index: photos.indexOf(f), caption })} />
 *   {lightbox && (
 *     <Lightbox photos={lightbox.photos} index={lightbox.index}
 *               caption={lightbox.caption} onClose={() => setLightbox(null)} />
 *   )}
 *
 * `photos` is an array of `{ url, filename }`.
 */
export default function Lightbox({ photos, index = 0, caption, onClose }) {
  const [current, setCurrent] = useState(index);
  const count = photos.length;

  const go = useCallback((delta) => {
    setCurrent(c => (c + delta + count) % count);
  }, [count]);

  // ←/→ to navigate, Esc to close.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && count > 1) go(-1);
      else if (e.key === 'ArrowRight' && count > 1) go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, count, onClose]);

  // Lock background scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (!count) return null;
  const photo = photos[current];

  return (
    <Overlay className={styles.backdrop} onClose={onClose}>
      <button className={styles.close} onClick={onClose} aria-label="Close">✕</button>

      {count > 1 && (
        <button
          className={`${styles.nav} ${styles.prev}`}
          onClick={() => go(-1)}
          aria-label="Previous photo"
        >‹</button>
      )}

      <div className={styles.stage}>
        <img className={styles.image} src={photo.url} alt={photo.filename || 'Photo'} />
        {(caption || count > 1) && (
          <div className={styles.bar}>
            {caption && <span className={styles.caption}>{caption}</span>}
            {count > 1 && <span className={styles.counter}>{current + 1} / {count}</span>}
          </div>
        )}
      </div>

      {count > 1 && (
        <button
          className={`${styles.nav} ${styles.next}`}
          onClick={() => go(1)}
          aria-label="Next photo"
        >›</button>
      )}
    </Overlay>
  );
}
